import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { listFundraising, getFundraisingMeta, getFundraisingGroups, getFundraisingFunds, getFundraisingCharts } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';
import FundFlow from '../components/FundFlow';
import FundraisingCharts from '../components/FundraisingCharts';

const PAGE_SIZE = 20;

const ROUND_COLOR = {
  Seed: '#10b981', 'Series A': '#3b82f6', 'Series B': '#6366f1',
  Private: '#8b5cf6', Strategic: '#f59e0b', Fund: '#0891b2', Unknown: '#64748b',
};

// The same fund can appear under name variants, e.g. "Payward" and
// "Payward (Kraken parent)". Collapse the trailing "(…)" for a canonical key
// so a fund isn't listed twice (and not once as lead + once as other).
const canonInvestor = (s) => String(s || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
function dedupInvestors(list) {
  const seen = new Set();
  const out = [];
  for (const raw of (list || [])) {
    const k = canonInvestor(raw).toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(canonInvestor(raw));
  }
  return out;
}
// Lead + other with cross-list dedup (a lead fund is dropped from "other").
function splitInvestors(lead, other) {
  const leadClean = dedupInvestors(lead);
  const leadKeys = new Set(leadClean.map(x => x.toLowerCase()));
  const otherClean = dedupInvestors(other).filter(x => !leadKeys.has(x.toLowerCase()));
  return { lead: leadClean, other: otherClean };
}

function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtDate(d, locale = 'vi-VN') {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// 'YYYY-MM-DD' in local time (matches announced_date's string format and
// <input type="date"> value format — no UTC/timezone drift).
function toDateInputValue(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
// Default date range, chosen so the list is never empty right after a new
// month starts (before many deals are announced):
//   - days 1–14  → 15th of last month … 15th of this month (spans the boundary)
//   - day 15 on  → 1st of this month … last day of this month
function defaultDateRange() {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), day = now.getDate();
  if (day < 15) {
    return {
      from: toDateInputValue(new Date(y, m - 1, 15)),
      to: toDateInputValue(new Date(y, m, 15)),
    };
  }
  return {
    from: toDateInputValue(new Date(y, m, 1)),
    to: toDateInputValue(new Date(y, m + 1, 0)),
  };
}

export default function FundraisingPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'en' ? 'en-US' : 'vi-VN';
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // Summary stats for the ACTIVE filter (so the header totals match the list).
  const [stats, setStats] = useState({ totalRaised: 0, totalProjects: 0 });
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [roundType, setRoundType] = useState('');
  const [category, setCategory] = useState('');
  const [token, setToken] = useState('');
  const [dateFrom, setDateFrom] = useState(() => defaultDateRange().from);
  const [dateTo, setDateTo] = useState(() => defaultDateRange().to);
  const [sort, setSort] = useState('date');

  // Primary tab (above the filter). 'list' shares the filter with its two
  // grouping sub-tabs; 'funds' and 'charts' are standalone (no filter).
  const [tab, setTab] = useState('list'); // 'list' | 'funds' | 'charts'
  // Grouping within the list tab. 'none' = the default flat list.
  const [group, setGroup] = useState('none'); // 'none' | 'investor' | 'category'
  const [groups, setGroups] = useState([]);
  const [groupsReady, setGroupsReady] = useState(false);
  // Venture-fund flow view.
  const [funds, setFunds] = useState([]);
  const [fundsReady, setFundsReady] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  // Comparison charts.
  const [chartsData, setChartsData] = useState(null);
  const [chartsReady, setChartsReady] = useState(false);

  const sentinelRef = useRef(null);

  useEffect(() => {
    getFundraisingMeta().then(setMeta).catch(() => {});
  }, []);

  // Debounce free-text search
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 350);
    return () => clearTimeout(t);
  }, [qInput]);

  const loadFirst = useCallback(async () => {
    setReady(false);
    try {
      const { docs, total: t, totalRaised, totalProjects } = await listFundraising({ limit: PAGE_SIZE, skip: 0, sort, q, roundType, category, token, dateFrom, dateTo });
      setItems(docs);
      setTotal(t);
      setStats({ totalRaised: totalRaised || 0, totalProjects: totalProjects || 0 });
    } catch { /* silent */ }
    finally { setReady(true); }
  }, [sort, q, roundType, category, token, dateFrom, dateTo]);

  useEffect(() => { loadFirst(); }, [loadFirst]);

  const loadMore = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { docs, total: t } = await listFundraising({ limit: PAGE_SIZE, skip: items.length, sort, q, roundType, category, token, dateFrom, dateTo });
      setTotal(t);
      setItems(prev => {
        const have = new Set(prev.map(d => d._id));
        return [...prev, ...docs.filter(d => !have.has(d._id))];
      });
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [items.length, loading, sort, q, roundType, category, token, dateFrom, dateTo]);

  // Load grouped data when a grouped view is active (re-runs on filter change).
  useEffect(() => {
    if (tab !== 'list' || group === 'none') return undefined;
    let active = true;
    setGroupsReady(false);
    getFundraisingGroups({ by: group, q, roundType, category, token, dateFrom, dateTo })
      .then(r => { if (active) setGroups(r.groups || []); })
      .catch(() => { if (active) setGroups([]); })
      .finally(() => { if (active) setGroupsReady(true); });
    return () => { active = false; };
  }, [tab, group, q, roundType, category, token, dateFrom, dateTo]);

  // Venture-fund flow data (loaded once when entering the funds tab).
  useEffect(() => {
    if (tab !== 'funds' || fundsReady) return undefined;
    let active = true;
    getFundraisingFunds()
      .then(r => { if (active) { setFunds(r.funds || []); setSelectedFund((r.funds || [])[0]?.name || null); } })
      .catch(() => { if (active) setFunds([]); })
      .finally(() => { if (active) setFundsReady(true); });
    return () => { active = false; };
  }, [tab, fundsReady]);

  // Chart aggregates (loaded once when entering the charts tab).
  useEffect(() => {
    if (tab !== 'charts' || chartsReady) return undefined;
    let active = true;
    getFundraisingCharts()
      .then(r => { if (active) setChartsData(r); })
      .catch(() => { if (active) setChartsData(null); })
      .finally(() => { if (active) setChartsReady(true); });
    return () => { active = false; };
  }, [tab, chartsReady]);

  useEffect(() => {
    if (tab !== 'list' || group !== 'none') return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      es => { if (es[0].isIntersecting && !loading && ready && items.length < total) loadMore(); },
      { rootMargin: '300px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // tab/group are deps so the observer re-attaches to the sentinel after
    // leaving and returning to the default list (it unmounts in between).
  }, [tab, group, loading, ready, items.length, total, loadMore]);

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>{t('fundraising.title')}</h2>
          <p style={s.subtitle}>
            {ready ? t('fundraising.subtitleReady', { count: total, amount: fmtUsd(stats.totalRaised) }) : t('fundraising.subtitleLoading')}
          </p>
        </div>
      </div>

      {/* Primary tabs — always on top of the content. */}
      <div style={s.viewBar}>
        {[['list', t('fundraising.tabList')], ['funds', t('fundraising.tabFunds')], ['charts', t('fundraising.tabCharts')]].map(([v, label]) => (
          <button
            key={v}
            type="button"
            style={tab === v ? s.viewBtnActive : s.viewBtn}
            onClick={(e) => { setTab(v); e.currentTarget.blur(); }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'list' && (
        <>
          {/* Filter block — date range on top, then the other filters. */}
          <div style={s.filterBlock}>
            <div style={s.dateRow}>
              <span style={s.filterHint}>{t('fundraising.dateRangeLabel')}</span>
              <input
                type="date" style={s.dateInput} value={dateFrom}
                max={dateTo || undefined}
                onChange={e => setDateFrom(e.target.value)}
              />
              <span style={s.dateSep}>→</span>
              <input
                type="date" style={s.dateInput} value={dateTo}
                min={dateFrom || undefined}
                onChange={e => setDateTo(e.target.value)}
              />
              <button
                type="button" style={s.resetDateBtn}
                title={t('fundraising.resetDateTitle')}
                onClick={() => { const r = defaultDateRange(); setDateFrom(r.from); setDateTo(r.to); }}
              >
                ↺
              </button>
            </div>
            <div style={s.filterRow}>
              <input
                style={s.search}
                placeholder={t('fundraising.searchPlaceholder')}
                value={qInput}
                onChange={e => setQInput(e.target.value)}
              />
              <select style={s.select} value={roundType} onChange={e => setRoundType(e.target.value)}>
                <option value="">{t('fundraising.allRoundTypes')}</option>
                {meta?.roundTypes.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <select style={s.select} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">{t('fundraising.allCategories')}</option>
                {meta?.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select style={s.select} value={token} onChange={e => setToken(e.target.value)}>
                <option value="">{t('fundraising.allTokens')}</option>
                {meta?.tokens?.map(tk => <option key={tk} value={tk}>${tk}</option>)}
              </select>
              <select style={s.select} value={sort} onChange={e => setSort(e.target.value)}>
                <option value="date">{t('fundraising.sortNewest')}</option>
                <option value="amount">{t('fundraising.sortAmount')}</option>
              </select>
            </div>
          </div>

          <div style={s.statRow}>
            <StatTile label={t('fundraising.statTotalDeals')} value={total} />
            <StatTile label={t('fundraising.statTotalProjects')} value={stats.totalProjects} />
            <StatTile label={t('fundraising.statTotalRaised')} value={fmtUsd(stats.totalRaised)} />
            <StatTile label={t('fundraising.statShowing')} value={group === 'none' ? `${items.length}/${total}` : t('fundraising.statShowingGroups', { count: groups.length })} />
          </div>

          {/* Grouping sub-tabs — between the filter and the list content. */}
          <div style={s.subTabBar}>
            {[['none', t('fundraising.subTabDefault')], ['investor', t('fundraising.subTabInvestor')], ['category', t('fundraising.subTabCategory')]].map(([v, label]) => (
              <button
                key={v}
                type="button"
                style={group === v ? s.subTabActive : s.subTabBtn}
                onClick={(e) => { setGroup(v); e.currentTarget.blur(); }}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      {tab === 'charts' ? (
        !chartsReady ? (
          <div style={s.loading}>{t('fundraising.loadingCharts')}</div>
        ) : !chartsData ? (
          <div style={s.empty}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{t('fundraising.noChartData')}</div>
          </div>
        ) : (
          <FundraisingCharts data={chartsData} />
        )
      ) : tab === 'funds' ? (
        !fundsReady ? (
          <div style={s.loading}>{t('fundraising.loadingFunds')}</div>
        ) : funds.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏛️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{t('fundraising.noFunds')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('fundraising.noFundsSub')}</div>
          </div>
        ) : (
          <>
            <div style={s.fundPicker}>
              {funds.map(f => (
                <button
                  key={f.name}
                  type="button"
                  style={selectedFund === f.name ? s.fundChipActive : s.fundChip}
                  onClick={(e) => { setSelectedFund(f.name); e.currentTarget.blur(); }}
                >
                  {f.name} <span style={s.fundChipCount}>· {t('fundraising.fundChipProjects', { count: f.investmentCount })}</span>
                </button>
              ))}
            </div>
            <FundFlow fund={funds.find(f => f.name === selectedFund) || funds[0]} />
          </>
        )
      ) : group !== 'none' ? (
        !groupsReady ? (
          <div style={s.loading}>{t('fundraising.loadingGroups')}</div>
        ) : groups.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{t('fundraising.noData')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('fundraising.noDataSub')}</div>
          </div>
        ) : (
          <div style={s.groupList}>
            {groups.map(g => <GroupCard key={g.key} g={g} by={group} dateLocale={dateLocale} />)}
          </div>
        )
      ) : !ready ? (
        <div style={s.loading}>{t('fundraising.loadingList')}</div>
      ) : items.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            {t('fundraising.noDeals')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {t('fundraising.noDataSub')}
          </div>
        </div>
      ) : (
        <>
          <div style={s.tableScroll}>
            <div style={s.tableWrap}>
              <div style={s.thead}>
                <span style={{ ...s.th, flex: 2 }}>{t('fundraising.colProject')}</span>
                <span style={s.th}>{t('fundraising.colRound')}</span>
                <span style={s.th}>{t('fundraising.colAmount')}</span>
                <span style={{ ...s.th, flex: 1.4 }}>{t('fundraising.colCategory')}</span>
                <span style={{ ...s.th, flex: 1.4 }}>{t('fundraising.colLeadInvestors')}</span>
                <span style={{ ...s.th, flex: 1.6 }}>{t('fundraising.colOtherInvestors')}</span>
                <span style={{ ...s.th, flex: 1.2 }} title={t('fundraising.colImpactedTokensTitle')}>
                  {t('fundraising.colImpactedTokens')}
                </span>
                <span style={s.th}>{t('fundraising.colDate')}</span>
                <span style={s.th}>{t('fundraising.colSource')}</span>
              </div>
              {items.map(d => <DealRow key={d._id} d={d} dateLocale={dateLocale} />)}
            </div>
          </div>
          {items.length < total && <div ref={sentinelRef} style={{ height: 1 }} />}
          {loading && (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
              {t('fundraising.loadingMore')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div style={s.tile}>
      <span style={s.tileLabel}>{label}</span>
      <span style={s.tileValue}>{value}</span>
    </div>
  );
}

// A collapsible group: a fund or a category, with its aggregated stats and
// (when expanded) the deals inside it.
function GroupCard({ g, by, dateLocale }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div style={s.group}>
      <button type="button" style={s.groupHead} onClick={() => setOpen(o => !o)}>
        <span style={s.groupKey}>
          {open ? '▾' : '▸'} {by === 'investor' ? '🏦' : '🏷️'} {g.key}
        </span>
        <span style={s.groupStats}>
          <span style={s.groupStat}><b>{g.dealCount}</b> {t('fundraising.unitDeals')}</span>
          <span style={s.groupStat}><b>{g.projectCount}</b> {t('fundraising.unitProjects')}</span>
          <span style={{ ...s.groupStat, color: 'var(--text)' }}><b>{fmtUsd(g.totalRaised)}</b></span>
        </span>
      </button>
      {open && (
        <div style={s.groupBody}>
          {(g.deals || []).map(d => {
            const color = ROUND_COLOR[d.round_type] || '#64748b';
            return (
              <div key={d._id} style={s.groupDeal}>
                <span style={{ ...s.gdCell, flex: 2, fontWeight: 700, color: 'var(--text)' }}>{d.project}</span>
                <span style={s.gdCell}>
                  <span style={{ ...s.roundBadge, color, borderColor: color + '55', background: color + '14' }}>{d.round_type}</span>
                </span>
                <span style={{ ...s.gdCell, fontWeight: 700, color: 'var(--text)' }}>{fmtUsd(d.amount_usd)}</span>
                <span style={{ ...s.gdCell, flex: 1.6 }}>{(d.categories || []).join(', ') || '—'}</span>
                <span style={s.gdCell}>{fmtDate(d.announced_date, dateLocale)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DealRow({ d, dateLocale }) {
  const color = ROUND_COLOR[d.round_type] || '#64748b';
  const inv = splitInvestors(d.lead_investors, d.other_investors);
  return (
    <div style={s.row} title={d.description || ''}>
      <span style={{ ...s.td, flex: 2, fontWeight: 700, color: 'var(--text)' }}>{d.project}</span>
      <span style={s.td}>
        <span style={{ ...s.roundBadge, color, borderColor: color + '55', background: color + '14' }}>
          {d.round_type}
        </span>
      </span>
      <span style={{ ...s.td, fontWeight: 700, color: 'var(--text)' }}>{fmtUsd(d.amount_usd)}</span>
      <span style={{ ...s.td, flex: 1.4 }}>
        {(d.categories || []).join(', ') || '—'}{d.chain ? <span style={s.tdSub}> · {d.chain}</span> : null}
      </span>
      <span style={{ ...s.td, flex: 1.4, wordBreak: 'break-word' }}>
        {inv.lead.join(', ') || '—'}
      </span>
      <span style={{ ...s.td, flex: 1.6, wordBreak: 'break-word' }}>
        {inv.other.join(', ') || '—'}
      </span>
      <span style={{ ...s.td, flex: 1.2 }}>
        {(d.impacted_tokens || []).length > 0
          ? (
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {d.impacted_tokens.map(t => <span key={t} style={s.tokenBadge}>${t}</span>)}
            </span>
          )
          : '—'}
      </span>
      <span style={s.td}>{fmtDate(d.announced_date)}</span>
      <span style={s.td}>
        {d.sources?.[0]
          ? <a href={d.sources[0].url} target="_blank" rel="noreferrer" style={s.sourceLink}>{d.sources[0].outlet} ↗</a>
          : '—'}
      </span>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 16 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  statRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 },
  tile: {
    display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 12, padding: '12px 16px', minWidth: 0,
  },
  tileLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 },
  tileValue: { fontSize: 18, fontWeight: 800, color: 'var(--text)', wordBreak: 'break-word' },

  // Filter block: a white card with the date range on top, other filters below.
  filterBlock: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
    padding: '12px 14px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10,
  },
  dateRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterHint: { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 },
  filterBar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 },
  search: {
    flex: '2 1 220px', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 12px', fontSize: 13, outline: 'none', minWidth: 0,
  },
  select: {
    flex: '1 1 160px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-muted)', padding: '8px 12px', fontSize: 12, cursor: 'pointer', minWidth: 0,
  },
  dateGroup: { display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 260px', minWidth: 0 },
  dateInput: {
    flex: '1 1 110px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-muted)', padding: '7px 10px', fontSize: 12, cursor: 'pointer', minWidth: 0,
  },
  dateSep: { color: 'var(--text-dim)', fontSize: 12, flexShrink: 0 },
  resetDateBtn: {
    flexShrink: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text-muted)', width: 30, height: 30, fontSize: 13, cursor: 'pointer',
  },

  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  tableScroll: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '8px 14px 14px', minWidth: 1080 },
  thead: { display: 'flex', gap: 12, padding: '10px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 },
  th: { flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.7, textTransform: 'uppercase', minWidth: 0 },
  row: { display: 'flex', gap: 12, alignItems: 'center', padding: '11px 8px', borderBottom: '1px solid var(--border)', minWidth: 0 },
  td: { flex: 1, fontSize: 12, color: 'var(--text-dim)', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' },
  tdSub: { color: 'var(--text-dim)', fontSize: 11 },
  roundBadge: { fontSize: 10, fontWeight: 700, border: '1px solid', borderRadius: 7, padding: '2px 9px', whiteSpace: 'nowrap' },
  tokenBadge: {
    fontSize: 10, fontWeight: 700, color: '#dc2626', background: 'rgba(220,38,38,0.08)',
    border: '1px solid rgba(220,38,38,0.25)', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap',
  },
  sourceLink: { fontSize: 11, color: 'var(--accent)', textDecoration: 'none' },

  // View-mode selector — viewBtn/viewBtnActive (and subTabBtn/fundChip
  // below) are two fully self-contained objects, always used as a straight
  // either/or replacement, never spread/merged: {...viewBtn, ...(active ?
  // viewBtnActive : {})} leaves a previously-active button's outline/shadow
  // stuck on it after it goes back to inactive, since only the KEYS present
  // in the *Active object get reset when the condition flips.
  viewBar: { display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' },
  viewBtn: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, outline: 'none',
    color: 'var(--text-muted)', padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: 'none',
  },
  viewBtnActive: {
    background: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 8, outline: 'none',
    color: 'var(--text)', padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 0 0 1px var(--accent)',
  },

  // Grouping sub-tabs (inside the list tab, below the filter)
  subTabBar: { display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' },
  subTabBtn: {
    background: 'transparent', border: '1px solid var(--border)', borderRadius: 20, outline: 'none',
    color: 'var(--text-muted)', padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  subTabActive: {
    background: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 20, outline: 'none',
    color: '#fff', padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },

  // Fund flow view
  fundPicker: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  fundChip: {
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, outline: 'none',
    color: 'var(--text-muted)', padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: 'none',
  },
  fundChipActive: {
    background: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 8, outline: 'none',
    color: 'var(--text)', padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 0 0 1px var(--accent)',
  },
  fundChipCount: { color: 'var(--text-dim)', fontWeight: 500 },

  // Grouped view
  groupList: { display: 'flex', flexDirection: 'column', gap: 8 },
  group: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  groupHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit',
    background: 'transparent', border: 'none', padding: '12px 16px', color: 'var(--text)',
  },
  groupKey: { fontSize: 14, fontWeight: 800, color: 'var(--text)', wordBreak: 'break-word', minWidth: 0 },
  groupStats: { display: 'flex', gap: 14, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' },
  groupStat: { fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  groupBody: { borderTop: '1px solid var(--border)', padding: '4px 16px 10px' },
  groupDeal: { display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', minWidth: 0 },
  gdCell: { flex: 1, fontSize: 12, color: 'var(--text-dim)', minWidth: 0, wordBreak: 'break-word' },
};
